import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Search, X } from "lucide-react";
import type { Doc } from "@alfred/convex/_generated/dataModel";
import { api } from "@alfred/convex/_generated/api";
import { KanbanBoard } from "~/components/KanbanBoard";
import { KANBAN_META, KANBAN_ORDER } from "~/lib/kanban";

export const Route = createFileRoute("/")({
  component: BoardPage,
});

/** Does a ticket match the search query (title, description, or number)? */
function ticketMatches(ticket: Doc<"tickets">, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    (ticket.jiraKey ?? "").toLowerCase().includes(needle) ||
    (ticket.title ?? "").toLowerCase().includes(needle) ||
    (ticket.description ?? "").toLowerCase().includes(needle)
  );
}

function BoardPage() {
  const tickets = useQuery(api.tickets.list, {});
  const counts = useQuery(api.tickets.counts, {});
  const [query, setQuery] = React.useState("");

  const trimmed = query.trim();

  const filtered = React.useMemo(() => {
    if (!tickets) return tickets;
    if (!trimmed) return tickets;
    return tickets.filter((t) => ticketMatches(t, trimmed));
  }, [tickets, trimmed]);

  // Keep the header pill counts in sync with what's actually shown: while
  // searching, count the filtered matches per lane; otherwise use the
  // server-side totals (and fall back to them while tickets are loading).
  const laneCounts = React.useMemo(() => {
    if (!trimmed || !filtered) return null;
    const c: Record<string, number> = {};
    for (const t of filtered) {
      const lane = (KANBAN_ORDER as string[]).includes(t.status)
        ? t.status
        : "todo";
      c[lane] = (c[lane] ?? 0) + 1;
    }
    return c;
  }, [filtered, trimmed]);

  const displayCounts = laneCounts ?? counts;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-border/70 bg-background/85 px-4 py-4 backdrop-blur lg:px-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              Alfred's Kanban
            </h1>
            <p className="text-xs text-muted-foreground">
              Jira tickets labeled <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">alfred</code> flow through these lanes automatically.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
            {KANBAN_ORDER.map((s) => (
              <div
                key={s}
                className="flex items-center gap-1.5 rounded-full border border-border bg-card px-2 py-1"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${KANBAN_META[s].bar}`} />
                <span>{KANBAN_META[s].label}</span>
                <span className="font-semibold text-foreground">
                  {displayCounts?.[s] ?? 0}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setQuery("");
              }}
              placeholder="Search tickets by number, title, or description…"
              aria-label="Search tickets"
              className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {trimmed && filtered && (
            <span className="text-[11px] text-muted-foreground">
              {filtered.length} match{filtered.length === 1 ? "" : "es"}
            </span>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 pt-3">
        <KanbanBoard tickets={filtered} />
      </div>
    </div>
  );
}
